/* eslint-disable no-async-promise-executor */
import { get } from 'lodash';
import router from '@/router';
import { frameInRoutes } from '@/router/routes';
import asyncRouter from '@/router/asyncRouter';

// 设置文件
import setting from '@/config/setting';

// 判定是否需要缓存
const isKeepAlive = (data) => get(data, 'meta.keepAlive', true);

export default {
  namespaced: true,
  state: {
    // 可以在多页 tab 模式下显示的页面
    pool: [],
    // 当前显示的多页面列表
    opened: setting.page.opened,
    // 已经加载多标签页数据 https://github.com/d2-projects/d2-admin/issues/201
    openedLoaded: false,
    // 当前页面
    current: '',
    currentAliveName: '',
    // 需要缓存的页面 name
    keepAlive: ['exception-404', 'exception-500', 'authorityPoint', 'authorityRole'],
    // 是否已经检验权限与路由
    hasCheckPerm: false,
  },
  actions: {
    /**
     * @description 确认已经加载多标签页数据 https://github.com/d2-projects/d2-admin/issues/201
     * @param {Object} context
     */
    isLoaded({ state }) {
      if (state.value) return Promise.resolve();
      return new Promise((resolve) => {
        const timer = setInterval(() => {
          if (state.openedLoaded) {
            resolve(clearInterval(timer));
          }
        }, 10);
      });
    },
    /**
     * @class opened
     * @description 从持久化数据载入标签页列表
     * @param {Object} context
     */
    openedLoad({ state, commit, dispatch }) {
      return new Promise(async (resolve) => {
        // store 赋值
        const value = await dispatch(
          'system/db/get',
          {
            dbName: 'sys',
            path: 'page.opened',
            defaultValue: setting.page.opened,
            user: true,
          },
          { root: true }
        );
        // 在处理函数中进行数据优化 过滤掉现在已经失效的页签或者已经改变了信息的页签
        // 以 fullPath 字段为准
        // 如果页面过多的话可能需要优化算法
        // valid 有效列表 1, 1, 0, 1 => 有效, 有效, 失效, 有效
        const valid = [];
        // 处理数据
        state.opened = value
          .map((opened) => {
            // 忽略首页
            if (opened.path === '/home') {
              valid.push(1);
              return opened;
            }
            // 尝试在所有的支持多标签页的页面里找到 name 匹配的页面
            const find = state.pool.find((item) => item.meta.tabId === opened.meta.tabId);
            // 记录有效或无效信息
            valid.push(find ? 1 : 0);
            // 返回合并后的数据 新的覆盖旧的
            // 新的数据中一般不会携带 params 和 query, 所以旧的参数会留存
            return Object.assign({}, opened, find);
          })
          .filter((opened, index) => valid[index] === 1);
        // 标记已经加载多标签页数据 https://github.com/d2-projects/d2-admin/issues/201
        state.openedLoaded = true;
        // 根据 opened 数据生成缓存设置
        commit('keepAliveRefresh');
        // end
        resolve();
      });
    },
    /**
     * 将 opened 属性赋值并持久化 在这之前请先确保已经更新了 state.opened
     * @param {Object} context
     */
    opened2db({ state, dispatch }) {
      return new Promise(async (resolve) => {
        // 设置数据
        dispatch(
          'system/db/set',
          {
            dbName: 'sys',
            path: 'page.opened',
            value: state.opened,
            user: true,
          },
          { root: true }
        );
        // end
        resolve();
      });
    },
    /**
     * @class opened
     * @description 更新页面列表上的某一项
     * @param {Object} context
     * @param {Object} payload { index, params, query, fullPath } 路由信息
     */
    openedUpdate(
      { state, commit, dispatch },
      { index, params, query, fullPath, path, name, meta }
    ) {
      return new Promise(async (resolve) => {
        // 更新页面列表某一项
        const page = state.opened[index];
        page.params = params || page.params;
        page.query = query || page.query;
        page.fullPath = fullPath || page.fullPath;
        page.path = path || page.path;
        page.name = name || page.name;
        page.meta = { ...meta };
        // 替换掉旧的数组项
        state.opened.splice(index, 1, page);
        if (isKeepAlive(page)) {
          commit('keepAlivePush', page.meta.aliveName);
        }
        // 持久化
        await dispatch('opened2db');
        // end
        resolve();
      });
    },
    /**
     * @class opened
     * @description 新增一个 tag (打开一个页面)
     * @param {Object} context
     * @param {Object} payload new tag info
     */
    add({ state, commit, dispatch }, { tag, params, query, fullPath, path, meta, name }) {
      return new Promise(async (resolve) => {
        // 设置新的 tag 在新打开一个以前没打开过的页面时使用
        const newTag = tag;
        newTag.params = params || newTag.params;
        newTag.query = query || newTag.query;
        newTag.fullPath = fullPath || newTag.fullPath;
        newTag.path = path || newTag.path;
        // newTag.meta = meta || newTag.meta;
        newTag.meta = { ...meta };
        newTag.name = name || newTag.name;

        // 添加进当前显示的页面数组
        state.opened.push(newTag);
        // 如果这个页面需要缓存 将其添加到缓存设置
        if (isKeepAlive(newTag)) {
          commit('keepAlivePush', newTag.meta.aliveName);
        }
        // 持久化
        await dispatch('opened2db');
        // end
        resolve();
      });
    },
    /**
     * @class current
     * @description 打开一个新的页面
     * @param {Object} context
     * @param {Object} payload 从路由钩子的 to 对象上获取 { name, params, query, fullPath } 路由信息
     */
    open({ state, commit, dispatch }, { params, query, fullPath, path, name, meta }) {
      const { tabId } = meta;
      return new Promise(async (resolve) => {
        if (path === '/login') {
          resolve();
          return;
        }
        // 已经打开的页面
        const { opened } = state;
        // 判断此页面是否已经打开 并且记录位置
        let pageOpendIndex = 0;
        const pageOpend = opened.find((page, index) => {
          const same = page.meta.tabId === tabId;
          pageOpendIndex = same ? index : pageOpendIndex;
          return same;
        });
        if (pageOpend) {
          // 页面以前打开过
          await dispatch('openedUpdate', {
            index: pageOpendIndex,
            params,
            query,
            fullPath,
            path,
            name,
            meta,
          });
        } else {
          // 页面以前没有打开过
          const page = state.pool.find((t) => t.meta.tabId === tabId);
          // 如果这里没有找到 page 代表这个路由虽然在框架内 但是不参与标签页显示
          if (page) {
            await dispatch('add', {
              tag: Object.assign({}, page),
              params,
              query,
              fullPath,
              path,
              name,
              meta,
            });
          }
        }

        commit('currentSet', tabId);
        //
        commit('currentAliveNameSet', meta.aliveName || path);
        // end
        resolve();
      });
    },
    /**
     * @class opened
     * @description 关闭一个 tag (关闭一个页面)
     * @param {Object} context
     * @param {Object} payload { tabId: 要关闭的标签名字,to:新的路由 }
     */
    close({ state, commit, dispatch }, { tabId, to = {} }) {
      return new Promise(async (resolve) => {
        // 下个新的页面
        let newPage = state.opened[0];
        const isCurrent = state.current === tabId;
        // 如果关闭的页面就是当前显示的页面
        if (isCurrent) {
          // 去找一个新的页面
          const len = state.opened.length;
          for (let i = 1; i < len; i += 1) {
            if (state.opened[i].meta.tabId === tabId) {
              if (i < len - 1) {
                newPage = state.opened[i + 1];
              } else {
                newPage = state.opened[i - 1];
              }
              break;
            }
          }
        }
        // 找到这个页面在已经打开的数据里是第几个
        const index = state.opened.findIndex((page) => page.meta.tabId === tabId);
        if (index >= 0) {
          // 如果这个页面是缓存的页面 将其在缓存设置中删除
          commit('keepAliveRemove', state.opened[index].name);
          // 更新数据 删除关闭的页面
          state.opened.splice(index, 1);
        }
        // 持久化
        await dispatch('opened2db');
        // 最后需要判断是否需要跳到首页
        if (isCurrent) {
          const { path = '', params = {}, query = {} } = newPage;
          const routerObj = {
            path,
            params,
            query,
          };
          if (Object.keys(to).length > 0) {
            router.push(to);
          } else {
            router.push(routerObj);
          }
        }
        // end
        resolve();
      });
    },

    /**
     * 移动最后一个元素到指定位置
     * @param {*} state
     * @param {*} index
     */
    moveLastToIndex({ state }, index) {
      return new Promise(async (resolve) => {
        if (index + 1 !== state.opened.length) {
          state.opened.splice(index, 0, state.opened.pop());
        }
        resolve();
      });
    },

    /**
     * @class opened
     * @description 关闭当前标签左边的标签
     * @param {Object} context
     * @param {Object} payload { pageSelect: 当前选中的 tabId }
     */
    closeLeft({ state, commit, dispatch }, { pageSelect } = {}) {
      return new Promise(async (resolve) => {
        const pageAim = pageSelect || state.current;
        let currentIndex = 0;
        state.opened.forEach((page, index) => {
          if (page.meta.tabId === pageAim) {
            currentIndex = index;
          }
        });
        if (currentIndex > 0) {
          // 删除打开的页面 并在缓存设置中删除
          state.opened
            .splice(1, currentIndex - 1)
            .forEach(({ name }) => commit('keepAliveRemove', name));
        }
        state.current = pageAim;
        if (router.app.$route.path !== pageAim) {
          router.push(pageAim);
        }
        // 持久化
        await dispatch('opened2db');
        // end
        resolve();
      });
    },
    /**
     * @class opened
     * @description 关闭当前标签右边的标签
     * @param {Object} context
     * @param {Object} payload { pageSelect: 当前选中的 tabId }
     */
    closeRight({ state, commit, dispatch }, { pageSelect } = {}) {
      return new Promise(async (resolve) => {
        const pageAim = pageSelect || state.current;
        let currentIndex = 0;
        state.opened.forEach((page, index) => {
          if (page.meta.tabId === pageAim) {
            currentIndex = index;
          }
        });
        // 删除打开的页面 并在缓存设置中删除
        state.opened
          .splice(currentIndex + 1)
          .forEach(({ name }) => commit('keepAliveRemove', name));
        // 设置当前的页面
        state.current = pageAim;
        if (router.app.$route.path !== pageAim) {
          router.push(pageAim);
        }
        // 持久化
        await dispatch('opened2db');
        // end
        resolve();
      });
    },
    /**
     * @class opened
     * @description 关闭当前激活之外的 tag
     * @param {Object} context
     * @param {Object} payload { pageSelect: 当前选中的 tabId }
     */
    closeOther({ state, commit, dispatch }, { pageSelect } = {}) {
      return new Promise(async (resolve) => {
        const pageAim = pageSelect || state.current;
        let currentIndex = 0;
        state.opened.forEach((page, index) => {
          if (page.meta.tabId === pageAim) {
            currentIndex = index;
          }
        });
        // 删除打开的页面数据 并更新缓存设置
        if (currentIndex === 0) {
          state.opened.splice(1).forEach(({ name }) => commit('keepAliveRemove', name));
        } else {
          state.opened
            .splice(currentIndex + 1)
            .forEach(({ name }) => commit('keepAliveRemove', name));
          state.opened
            .splice(1, currentIndex - 1)
            .forEach(({ name }) => commit('keepAliveRemove', name));
        }
        // 设置新的页面
        state.current = pageAim;
        if (router.app.$route.path !== pageAim) {
          router.push(pageAim);
        }
        // 持久化
        await dispatch('opened2db');
        // end
        resolve();
      });
    },
    /**
     * @class opened
     * @description 关闭所有 tag
     * @param {Object} context
     */
    closeAll({ state, commit, dispatch }) {
      return new Promise(async (resolve) => {
        // 删除打开的页面 并在缓存设置中删除
        state.opened.splice(1).forEach(({ name }) => commit('keepAliveRemove', name));
        // 持久化
        await dispatch('opened2db');
        // 关闭所有的标签页后需要判断一次现在是不是在首页
        if (router.app.$route.name !== 'home') {
          router.push({
            name: 'home',
          });
        }
        // end
        resolve();
      });
    },
    /**
     * @class page
     * @description 更新权限与路由关系，重新设置路由权限
     * @param {*} param0
     */
    updateRouter({ state, commit, dispatch, rootState }) {
      if (state.hasCheckPerm) {
        return Promise.resolve(false);
      }
      return new Promise(async (resolve, reject) => {
        try {
          await dispatch('system/user/userInfo', {}, { root: true });
          await dispatch('system/menu/getSideBarMenu', {}, { root: true });
          await dispatch('system/user/getInterConfig', {}, { root: true });
          const dynamicRoutes = asyncRouter(frameInRoutes, rootState.system.menu.permissionList);
          router.options.routes = [...dynamicRoutes, ...router.options.routes];
          router.options.routes = [...dynamicRoutes];
          router.addRoutes(dynamicRoutes);
          commit('setHasCheckPerm', true);
          resolve(true);
        } catch (err) {
          reject(err);
        }
      });
    },
    /**
     * tab数据元素移动
     */
    sortOrder({ state, dispatch }, { oldIndex, newIndex }) {
      return new Promise(async (resolve) => {
        state.opened.splice(
          oldIndex,
          1,
          ...state.opened.splice(newIndex, 1, state.opened[oldIndex])
        );
        // 持久化
        await dispatch('opened2db');
        // end
        resolve();
      });
    },
  },
  mutations: {
    /**
     * @class keepAlive
     * @description 从已经打开的页面记录中更新需要缓存的页面记录
     * @param {Object} state state
     */
    keepAliveRefresh(state) {
      state.keepAlive = [
        ...state.keepAlive,
        ...state.opened.filter((item) => isKeepAlive(item)).map((e) => e.meta.aliveName),
      ];
    },
    /**
     * @description 删除一个页面的缓存设置
     * @param {Object} state state
     * @param {String} name name
     */
    keepAliveRemove(state, name = state.currentAliveName) {
      const list = [...state.keepAlive];
      const index = list.findIndex((item) => item === name);
      if (index !== -1) {
        list.splice(index, 1);
        state.keepAlive = list;
      }
    },
    /**
     * @description 增加一个页面的缓存设置
     * @param {Object} state state
     * @param {String} name name
     */
    keepAlivePush(state, name) {
      const keep = [...state.keepAlive];
      // 不存在则计入数组
      if (keep.findIndex((item) => item === name) === -1) {
        keep.push(name);
      }
      state.keepAlive = keep;
    },
    /**
     * @description 清空页面缓存设置
     * @param {Object} state state
     */
    keepAliveClean(state) {
      state.keepAlive = [];
    },
    /**
     * @class current
     * @description 设置当前激活的页面 fullPath
     * @param {Object} state state
     * @param {String} fullPath new fullPath
     */
    currentSet(state, fullPath) {
      state.current = fullPath;
    },
    /**
     * @class current
     * @description 设置当前激活的页面 fullPath
     * @param {Object} state state
     * @param {String} name
     */
    currentAliveNameSet(state, name) {
      state.currentAliveName = name;
    },
    /**
     * @class pool
     * @description 保存 pool (候选池)
     * @param {Object} state state
     * @param {Array} routes routes
     */
    init(state, routes) {
      const pool = [];
      const push = function push(pages) {
        pages.forEach((route) => {
          if (route.children && route.children.length > 0) {
            push(route.children);
          } else if (!route.hidden) {
            // 修正路由meta信息字段 title、tabId
            const { name, path, meta } = route;
            const newMeta = { title: '', tabId: '', ...meta };
            const { title, tabId } = newMeta;
            newMeta.title = title || name || '未命名';
            newMeta.tabId = tabId || path;
            // end
            pool.push({ meta: newMeta, name, path });
          }
        });
      };
      push(routes);
      state.pool = pool;
    },
    setHasCheckPerm(state, val) {
      state.hasCheckPerm = val;
    },
    setPageTitle(state, val) {
      state.pageTitle = val;
    },
    //
  },
};
